import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { money, moneyCompact, num, times } from "@owners/format";
import { api, useMe } from "../api";
import { HotelPicker, MoneyVar, Tile, hotelParam } from "../components/Numbers";

/**
 * Budget + Reforecast (SPEC §10). Current year: projected at Dec 31 (actual
 * year to date + reforecast), the owner waterfall, the DSCR watch list and
 * owner contributions. Prior years: Budget vs Actual.
 */

interface FY { projected: number | null; budget: number | null; lastYear: number | null }
interface Row { key: string; label: string; kind: string; sign: "rev" | "exp" | "profit" | "stat"; months: Array<number | null>; fy: FY }
interface W {
  revenue: number | null; noi: number | null; noiBudget: number | null; noiLastYear: number | null; ffePct: number; ffeReserve: number | null;
  lenderNoi: number | null; debtService: number; dscr: number | null; dscrCovenant: number | null;
  dscrStatus: "below" | "watch" | "ok" | "no_debt" | "no_data"; dscrShortfall: number | null;
  cashAfterDebtService: number | null; ownerCapex: number | null; ownerCash: number | null;
  distributionsPaid: number | null; q4Available: number | null; ownerContribution: number | null;
}
interface HotelW { id: string; name: string; hasData: boolean; actualMonths: number | null; loans: number; notes: string | null; waterfall: W }
interface BudgetRes {
  year: number; currentYear: number; years: number[]; actualMonths: number; reforecastAsOf: string | null;
  rows: Row[]; hotels: HotelW[]; total: W | null; watchList: HotelW[]; contributions: HotelW[]; inputsMissing: string[]; canEdit: boolean;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const Tbd = () => <span className="tbd">TBD</span>;
const m = (v: number | null) => (v === null ? <Tbd /> : money(v));
const title = (s: string) => s.trim() === "EBITDA" ? "Net operating income (EBITDA)"
  : s.toLowerCase().replace(/(^|[\s(\-/])([a-z])/g, (_x, p, c) => p + c.toUpperCase()).replace(/\bF&b\b/g, "F&B").replace(/\bA&g\b/g, "A&G").replace(/\bEbitda\b/g, "EBITDA");

export function BudgetReforecast() {
  const { data: me } = useMe();
  const [ids, setIds] = useState<string[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [showMonths, setShowMonths] = useState(false);
  const q = useQuery({
    queryKey: ["budget", year, ids],
    queryFn: () => api<BudgetRes>(`/api/budget?hotelIds=${hotelParam(ids)}${year ? `&year=${year}` : ""}`),
  });
  const d = q.data;
  const current = !d || d.year === d.currentYear;
  const t = d?.total;
  const noiRow = d?.rows.find((r) => r.label.trim() === "EBITDA");

  return (
    <>
      <div className="eyebrow">Owners Module · Budget + Reforecast</div>
      <h1>{current ? `${d?.year ?? ""} Budget — projected at Dec 31` : `${d.year} Budget vs Actual`}</h1>
      <div className="tabs">
        {(d?.years ?? []).map((y) => (
          <button key={y} className={(year ?? d!.currentYear) === y ? "on" : ""} onClick={() => setYear(y === d!.currentYear ? null : y)}>
            {y === d!.currentYear ? `${y} Budget` : `${y} Budget vs Actual`}
          </button>
        ))}
      </div>
      <div className="row" style={{ marginBottom: 16 }}>
        <HotelPicker value={ids} onChange={setIds} />
        {d && current && <span className="muted">Actual Jan–{MONTHS[Math.max(d.actualMonths - 1, 0)]}, reforecast {MONTHS[Math.min(d.actualMonths, 11)]}–Dec{ids.length !== 1 && (d.hotels.length > 1) ? " · several hotels are added together" : ""}</span>}
      </div>

      {q.isLoading ? <div className="card muted">Loading…</div> : q.error ? <div className="card error">{(q.error as Error).message}</div> : d && (
        <>
          {current && t && (
            <div className="tiles strip">
              <Tile label="Projected NOI" value={moneyCompact(t.noi)} sub={<><MoneyVar actual={t.noi} base={t.noiBudget} /> vs budget</>} />
              <Tile label="Year-end DSCR" value={t.debtService ? times(t.dscr) : "—"} sub={t.debtService ? `Lender NOI ${moneyCompact(t.lenderNoi)} ÷ debt service ${moneyCompact(t.debtService)}` : "No loans entered in Debt Compliance"} />
              <Tile label="Q4 available to distribute" tbd={t.q4Available === null} value={moneyCompact(t.q4Available)}
                sub={t.q4Available === null ? `Needs owner CapEx and distributions paid for ${num(d.inputsMissing.length)} hotels` : "After debt service, reserves and CapEx"} />
              <Tile label="Owner contributions" tbd={t.ownerContribution === null} value={num(d.contributions.length)}
                sub={d.contributions.length ? `${moneyCompact(t.ownerContribution)} needed` : "None needed"} />
            </div>
          )}

          {current && t && (
            <div className="row" style={{ alignItems: "flex-start", gap: 18 }}>
              <div className="card" style={{ flex: 1, minWidth: 360 }}>
                <h2>Cash to owners</h2>
                <table className="fin">
                  <tbody>
                    <tr><td>Projected NOI</td><td className="num">{m(t.noi)}</td></tr>
                    <tr><td>FF&amp;E reserve ({(t.ffePct * 100).toFixed(1)}% of {moneyCompact(t.revenue)} revenue)</td><td className="num">{m(t.ffeReserve === null ? null : -t.ffeReserve)}</td></tr>
                    <tr className="k-subtotal"><td>Lender NOI</td><td className="num">{m(t.lenderNoi)}</td></tr>
                    <tr><td>Debt service</td><td className="num">{money(-t.debtService)}</td></tr>
                    <tr><td>DSCR</td><td className="num">{t.debtService ? times(t.dscr) : "—"}</td></tr>
                    <tr className="k-subtotal"><td>Cash after debt service</td><td className="num">{m(t.cashAfterDebtService)}</td></tr>
                    <tr><td>Owner CapEx and PIP</td><td className="num">{m(t.ownerCapex === null ? null : -t.ownerCapex)}</td></tr>
                    <tr className="k-subtotal"><td>Owner cash</td><td className="num">{m(t.ownerCash)}</td></tr>
                    <tr><td>Already distributed this year</td><td className="num">{m(t.distributionsPaid === null ? null : -t.distributionsPaid)}</td></tr>
                    <tr className="k-total"><td>Q4 available to distribute</td><td className="num">{m(t.q4Available)}</td></tr>
                  </tbody>
                </table>
                <p className="muted" style={{ fontSize: 12 }}>
                  The SPEC's formula. The distribution policy itself (reserves to hold back, working capital, lender cash management) is still an open question.
                </p>
              </div>
              <div style={{ flex: 1, minWidth: 360 }}>
                <div className="card">
                  <h2>Watch list — year-end DSCR</h2>
                  {d.watchList.length === 0 ? <p className="muted">No hotel is projected below or within 0.10x of its covenant.</p> : (
                    <table>
                      <thead><tr><th>Hotel</th><th className="num">Lender NOI</th><th className="num">Debt service</th><th className="num">DSCR</th><th className="num">Covenant</th><th className="num">Shortfall</th></tr></thead>
                      <tbody>{d.watchList.map((h) => (
                        <tr key={h.id}>
                          <td>{h.name} {h.waterfall.dscrStatus === "below" ? <span className="pill bad-pill">Below</span> : <span className="pill warn-pill">Watch</span>}</td>
                          <td className="num">{moneyCompact(h.waterfall.lenderNoi)}</td><td className="num">{moneyCompact(h.waterfall.debtService)}</td>
                          <td className="num">{times(h.waterfall.dscr)}</td><td className="num">{h.waterfall.dscrCovenant !== null ? times(h.waterfall.dscrCovenant) : "1.00x*"}</td>
                          <td className="num">{h.waterfall.dscrShortfall !== null ? moneyCompact(h.waterfall.dscrShortfall) : "—"}</td>
                        </tr>))}</tbody>
                    </table>
                  )}
                </div>
                <div className="card">
                  <h2>Owner contributions needed</h2>
                  {d.contributions.length === 0 ? <p className="muted">{d.inputsMissing.length ? `Can't tell yet for ${num(d.inputsMissing.length)} hotels — owner CapEx or distributions paid not entered.` : "None."}</p> : (
                    <table><tbody>{d.contributions.map((h) => (
                      <tr key={h.id}><td>{h.name}</td><td className="num">{money(h.waterfall.ownerContribution)}</td></tr>))}</tbody></table>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h2 style={{ margin: 0 }}>{current ? "Profit & loss — projected vs budget" : "Profit & loss — actual vs budget"}</h2>
              <label className="muted"><input type="checkbox" checked={showMonths} onChange={(e) => setShowMonths(e.target.checked)} /> Show months</label>
            </div>
            {d.rows.length === 0 ? <p className="muted">No budget numbers for these hotels and this year yet.</p> : (
              <div style={{ overflowX: "auto" }}>
                <table className="fin">
                  <thead><tr>
                    <th />
                    {showMonths && MONTHS.map((mo, i) => <th key={mo} className="num">{mo}{current && <div className="muted" style={{ fontSize: 10 }}>{i < d.actualMonths ? "actual" : "reforecast"}</div>}</th>)}
                    <th className="num">{current ? "Projected" : "Actual"}</th><th className="num">Budget</th><th className="num">vs budget</th><th className="num">Last year</th>
                  </tr></thead>
                  <tbody>{d.rows.map((r) => r.kind === "header" ? (
                    <tr key={r.key} className="k-header"><td colSpan={showMonths ? 16 : 5}>{r.label}</td></tr>
                  ) : (
                    <tr key={r.key} className={`k-${r.kind}`}>
                      <td>{title(r.label)}</td>
                      {showMonths && r.months.map((v, i) => <td key={i} className="num">{v === null ? "—" : moneyCompact(v)}</td>)}
                      <td className="num">{money(r.fy.projected)}</td><td className="num">{money(r.fy.budget)}</td>
                      <td className="num"><MoneyVar actual={r.fy.projected} base={r.fy.budget} cost={r.sign === "exp"} /></td>
                      <td className="num">{money(r.fy.lastYear)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {!current && noiRow && <p className="muted" style={{ fontSize: 12 }}>NOI {moneyCompact(noiRow.fy.projected)} vs budget {moneyCompact(noiRow.fy.budget)}.</p>}
          </div>

          {current && d.canEdit && <OwnerInputs year={d.year} hotels={d.hotels} admin={me?.level === "admin"} />}
        </>
      )}
    </>
  );
}

/** Owner-side inputs per hotel (V006): the two numbers no system holds yet. */
function OwnerInputs({ year, hotels, admin }: { year: number; hotels: HotelW[]; admin: boolean }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Record<string, { capex: string; dist: string }>>({});
  const save = useMutation({
    mutationFn: (h: HotelW) => {
      const e = edit[h.id]!;
      return api(`${admin ? "/api/admin" : "/api"}/budget/inputs`, { method: "PUT", body: { hotelId: h.id, year, ownerCapex: e.capex, distributionsPaid: e.dist } });
    },
    onSuccess: (_r, h) => {
      setEdit((x) => { const n = { ...x }; delete n[h.id]; return n; });
      qc.invalidateQueries({ queryKey: ["budget"] }); qc.invalidateQueries({ queryKey: ["today-numbers"] });
    },
  });
  const val = (v: number | null) => (v === null ? "" : String(v));
  const cell = (h: HotelW, k: "capex" | "dist"): ReactNode => {
    const e = edit[h.id];
    const shown = e ? e[k] : val(k === "capex" ? h.waterfall.ownerCapex : h.waterfall.distributionsPaid);
    return <input style={{ width: 130, textAlign: "right" }} inputMode="decimal" value={shown} placeholder="TBD"
      onChange={(ev) => setEdit((x) => ({ ...x, [h.id]: { capex: x[h.id]?.capex ?? val(h.waterfall.ownerCapex), dist: x[h.id]?.dist ?? val(h.waterfall.distributionsPaid), [k]: ev.target.value } }))} />;
  };
  return (
    <div className="card">
      <h2>Owner inputs for {year}</h2>
      <p className="muted" style={{ marginTop: 0 }}>Owner-funded CapEx and PIP (not paid from FF&amp;E reserves) and distributions already paid this year. Everything else comes from the books and Debt Compliance.</p>
      <table>
        <thead><tr><th>Hotel</th><th className="num">Owner CapEx + PIP ($)</th><th className="num">Distributions paid ($)</th><th /></tr></thead>
        <tbody>{hotels.map((h) => (
          <tr key={h.id}>
            <td>{h.name}{!h.hasData && <span className="muted"> · no books yet</span>}</td>
            <td className="num">{cell(h, "capex")}</td><td className="num">{cell(h, "dist")}</td>
            <td>{edit[h.id] && <button className="btn" disabled={save.isPending} onClick={() => save.mutate(h)}>Save</button>}</td>
          </tr>))}</tbody>
      </table>
      {save.error && <p className="error">{(save.error as Error).message}</p>}
    </div>
  );
}
